import Big from 'big.js'
import { pricesRefreshInterval } from './config'
import { InternalInconsistencyError, ResponseError } from './errors'
import type {
  AssetsConfigType,
  ChainAsset,
  HistoricalPriceResponse,
  Portfolio,
  Portfolios,
  PriceRangeStore,
  PricesStore,
  RangeMap,
  RawHistoricalIndexAsset,
} from './interfaces'
import { z } from 'zod'
import { zeroExWrapperUrl } from 'abis/set/sets'

export const getPricesStore = (): PricesStore => {
  const data = JSON.parse(localStorage.getItem('cachedPrices') ?? '{}') as PricesStore
  return data
}

export const writePricesStore = (cachedPrices: PricesStore) => {
  const data = JSON.parse(localStorage.getItem('cachedPrices') ?? '{}') as PricesStore
  for (const asset in cachedPrices) {
    if (Object.prototype.hasOwnProperty.call(cachedPrices, asset)) {
      if (!data[asset]) {
        data[asset] = {}
      }
      for (const timestamp in cachedPrices[asset]) {
        if (Object.prototype.hasOwnProperty.call(cachedPrices[asset], timestamp)) {
          data[asset][timestamp] = cachedPrices[asset][timestamp]
        }
      }
    }
  }
  localStorage.setItem('cachedPrices', JSON.stringify(data))
}

export const getPriceRangesStore = (): PriceRangeStore => {
  const data = JSON.parse(localStorage.getItem('cachedPriceRanges') ?? '{}') as PriceRangeStore
  return data
}

export const writePriceRangesStore = (cachedPriceRanges: PriceRangeStore) => {
  const data = JSON.parse(localStorage.getItem('cachedPriceRanges') ?? '{}') as PriceRangeStore
  for (const asset in cachedPriceRanges) {
    const _a = asset as ChainAsset
    if (Object.prototype.hasOwnProperty.call(cachedPriceRanges, asset)) {
      if (!data[_a]) {
        data[_a] = {}
      }
      for (const rangeIndex in cachedPriceRanges[_a]) {
        if (Object.prototype.hasOwnProperty.call(cachedPriceRanges[_a], rangeIndex)) {
          data[_a][rangeIndex] = cachedPriceRanges[_a][rangeIndex]
        }
      }
    }
  }
  localStorage.setItem('cachedPriceRanges', JSON.stringify(data))
}

export function dayIndexToTimestamp(assetConfig: AssetsConfigType, dayIndex: number) {
  return dayIndex * assetConfig.refreshTimeIntervalGranularity
}

export function dayIndexToRangeIndex(assetConfig: AssetsConfigType, dayIndex: number) {
  if (assetConfig.maximumAllowedTimeRange == 0) throw Error() // TODO: Zero()
  return Math.floor(dayIndex / assetConfig.maximumAllowedRangeDays)
}

export function timestampToDayIndex(assetConfig: AssetsConfigType, timestamp: number) {
  if (assetConfig.refreshTimeIntervalGranularity === 0) throw Error() // TODO: Zero()
  return Math.floor(timestamp / assetConfig.refreshTimeIntervalGranularity)
}

const PriceRequestResponse = z.object({
  data: z.record(z.array(z.array(z.object({ price: z.string(), timestamp: z.number() })))),
  errors: z.array(z.object({ key: z.string(), reason: z.string() })).optional(),
})

export const requestHistoricalPrice = async (batches: RangeMap) => {
  const url = `${zeroExWrapperUrl}/price/historical`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(batches),
  })
  const response = await res.json()

  if (res.status !== 201) {
    throw new ResponseError(response)
  }
  const result = PriceRequestResponse.parse(response)

  return result as HistoricalPriceResponse
}

export const requestLastSyncedTimestamps = async () => {
  const url = `${zeroExWrapperUrl}/price/timestamps`
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })

  const resp = await response.json()
  if (response.status !== 200) {
    throw new ResponseError(resp)
  }
  return resp.data as { [symbol: ChainAsset]: number }
}

export function parsePortfolios(response: RawHistoricalIndexAsset[]): Portfolios[] {
  const portfolios: Portfolios[] = []

  for (const indexHistoryPart of response) {
    const symbol = indexHistoryPart.symbol
    const index = indexHistoryPart.id
    const chainID = indexHistoryPart.chainID
    for (const rawPortfolio of indexHistoryPart.history) {
      const timestamp = Number(rawPortfolio.timestamp)
      const assets: Portfolio = {}
      const totalSupply = Big(rawPortfolio.totalSupply)
      for (const rawAsset of rawPortfolio.assets) {
        assets[`${rawAsset.chainID}:${rawAsset.asset}`] = {
          chainId: +rawAsset.chainID,
          value: Big(rawAsset.balance),
        }
      }
      portfolios.push({
        symbol,
        chainID,
        index,
        totalSupply,
        timestamp,
        assets,
      })
    }
  }
  return portfolios
}

export function updateRange(
  priceRanges: PriceRangeStore,
  asset: ChainAsset,
  rangeIndex: number,
  rangeStart: number,
  rangeEnd: number,
) {
  if (rangeStart > rangeEnd) {
    throw new InternalInconsistencyError()
  }
  if (priceRanges[asset] === undefined) {
    priceRanges[asset] = {}
  }
  const currentRange = priceRanges[asset][rangeIndex]
  if (currentRange === undefined) {
    // insert a new range
    priceRanges[asset][rangeIndex] = [rangeStart, rangeEnd]
  } else {
    priceRanges[asset][rangeIndex] = [
      Math.min(rangeStart, currentRange[0]),
      Math.max(rangeEnd, currentRange[1]),
    ]
  }
}

export function isInsideCachedRange(
  config: AssetsConfigType,
  cachedPriceRanges: PriceRangeStore,
  asset: ChainAsset,
  cachedRangeIndex: number,
  rangeStart: number,
  rangeEnd: number,
  now: number,
  lastSyncAssetTimestamp: number,
) {
  const shouldUpdate = rangeEnd === now && lastSyncAssetTimestamp + pricesRefreshInterval < rangeEnd

  const cachedRange = cachedPriceRanges[asset]?.[cachedRangeIndex]
  // round range bounds to the closest day
  return (
    cachedRange !== undefined &&
    timestampToDayIndex(config, rangeStart) >= timestampToDayIndex(config, cachedRange[0]) &&
    timestampToDayIndex(config, rangeEnd) <= timestampToDayIndex(config, cachedRange[1]) &&
    !shouldUpdate
  )
}
